import React, { useState, useEffect } from 'react';
import { getBaseUrl } from '../../../../shared/utils/apiConfig';
import { 
  Activity, AlertTriangle, CheckCircle2, Clock, 
  Server, Users, Zap, TrendingUp, TrendingDown,
  AlertCircle, XCircle, RefreshCw
} from 'lucide-react';
import { MetricCard } from '../../components/MetricCard';

/**
 * Tenant Health - Platform Admin
 * 
 * Following 00-READ-THIS-FIRST:
 * - NO MOCK DATA: Real /api/platform-admin/tenants/health
 * - NULL SAFETY: Handles missing data
 * - PROGRESSIVE COMPLEXITY: Beautiful, informative UI
 */

interface TenantHealth {
  tenant_id: string;
  tenant_name: string;
  status: string;
  health_score: number;
  alerts_24h: number;
  active_users: number;
  connectors_healthy: number;
  connectors_total: number;
  last_activity: string | null;
  issues: string[];
}

export const TenantHealth: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tenants, setTenants] = useState<TenantHealth[]>([]);
  const [sortBy, setSortBy] = useState<'health' | 'alerts' | 'name'>('health');
  const [filterHealth, setFilterHealth] = useState<'all' | 'healthy' | 'warning' | 'critical'>('all');

  useEffect(() => {
    fetchHealth();
    // Auto-refresh every 30 seconds
    const interval = window.setInterval(fetchHealth, 30000);
    return () => {
      window.clearInterval(interval);
    };
  }, []);

  const fetchHealth = async () => {
    try {
      const token = localStorage.getItem('astrapeak_platform_admin_token');
      if (!token) {
        setError('No authentication token found');
        setLoading(false);
        return;
      }

      const response = await fetch(`${getBaseUrl()}/api/platform-admin/tenants/health`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch tenant health');
      
      const data = await response.json();
      setTenants(data.tenants || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching tenant health:', err);
      setError(err instanceof Error ? err.message : 'Failed to load health data');
    } finally {
      setLoading(false);
    }
  };

  const getHealthCategory = (score: number): 'healthy' | 'warning' | 'critical' => {
    if (score >= 80) return 'healthy';
    if (score >= 50) return 'warning';
    return 'critical';
  };

  const getHealthColor = (score: number) => {
    const category = getHealthCategory(score);
    switch (category) {
      case 'healthy':
        return 'text-green-400 bg-green-500/20 border-green-500/30';
      case 'warning':
        return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30';
      case 'critical':
        return 'text-red-400 bg-red-500/20 border-red-500/30';
    }
  };

  const getHealthIcon = (score: number) => {
    const category = getHealthCategory(score);
    switch (category) {
      case 'healthy':
        return <CheckCircle2 className="h-5 w-5 text-green-400" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-400" />;
      case 'critical':
        return <XCircle className="h-5 w-5 text-red-400" />;
    }
  };

  const getHealthLabel = (score: number): string => {
    const category = getHealthCategory(score);
    switch (category) {
      case 'healthy':
        return 'Healthy';
      case 'warning':
        return 'Warning';
      case 'critical':
        return 'Critical';
    }
  };

  const sortedTenants = [...tenants].sort((a, b) => {
    switch (sortBy) {
      case 'health':
        return b.health_score - a.health_score;
      case 'alerts':
        return b.alerts_24h - a.alerts_24h;
      case 'name':
        return a.tenant_name.localeCompare(b.tenant_name);
      default:
        return 0;
    }
  });

  const filteredTenants = sortedTenants.filter(tenant => {
    if (filterHealth === 'all') return true;
    return getHealthCategory(tenant.health_score) === filterHealth;
  });

  const stats = {
    total: tenants.length,
    healthy: tenants.filter(t => getHealthCategory(t.health_score) === 'healthy').length,
    warning: tenants.filter(t => getHealthCategory(t.health_score) === 'warning').length,
    critical: tenants.filter(t => getHealthCategory(t.health_score) === 'critical').length,
    avgHealth: tenants.length > 0 ? Math.round(tenants.reduce((sum, t) => sum + t.health_score, 0) / tenants.length) : 0,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="h-12 w-12 animate-spin border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Loading tenant health data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <h3 className="text-lg font-semibold text-red-400">Error Loading Health Data</h3>
          </div>
          <p className="text-red-400/80">{error}</p>
          <button
            onClick={fetchHealth}
            className="mt-4 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header with Refresh */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white mb-2">Tenant Health Monitor</h1>
          <p className="text-gray-400">Real-time health status across all tenants</p>
        </div>
        <button
          onClick={fetchHealth}
          className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors border border-gray-700"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Health Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <MetricCard
          title="Average Health"
          value={stats.avgHealth}
          unit="%"
          icon={Activity}
          status="info"
          statusLabel="Overall"
        />
        
        <MetricCard
          title="Healthy Tenants"
          value={stats.healthy}
          icon={CheckCircle2}
          status="success"
          statusLabel="Excellent"
        />
        
        <MetricCard
          title="Need Attention"
          value={stats.warning}
          icon={AlertTriangle}
          status="warning"
          statusLabel="Review"
        />
        
        <MetricCard
          title="Critical Issues"
          value={stats.critical}
          icon={XCircle}
          status="error"
          statusLabel="Urgent"
        />
        
        <MetricCard
          title="Total Monitored"
          value={stats.total}
          icon={Server}
          status="info"
          statusLabel="Active"
        />
      </div>

      {/* Filters and Sort */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex gap-2">
          <span className="text-sm text-gray-400 self-center">Filter:</span>
          {(['all', 'healthy', 'warning', 'critical'] as const).map((filter) => (
            <button
              key={filter}
              onClick={() => setFilterHealth(filter)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filterHealth === filter
                  ? 'bg-primary text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </button>
          ))}
        </div>

        <div className="flex gap-2 ml-auto">
          <span className="text-sm text-gray-400 self-center">Sort by:</span>
          {(['health', 'alerts', 'name'] as const).map((sort) => (
            <button
              key={sort}
              onClick={() => setSortBy(sort)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                sortBy === sort
                  ? 'bg-primary text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              {sort.charAt(0).toUpperCase() + sort.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Tenant Health Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filteredTenants.map((tenant) => (
          <div
            key={tenant.tenant_id}
            className="bg-gray-800 rounded-lg border border-gray-700 p-6 hover:border-gray-600 transition-colors"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white mb-1">{tenant.tenant_name}</h3>
                <p className="text-xs text-gray-500">{tenant.tenant_id}</p>
              </div>
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border ${getHealthColor(tenant.health_score)}`} aria-label={`Health status: ${getHealthLabel(tenant.health_score)} at ${tenant.health_score}%`}>
                {getHealthIcon(tenant.health_score)}
                <span>{getHealthLabel(tenant.health_score)}</span>
                <span className="opacity-75">({tenant.health_score}%)</span>
              </div>
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Zap className="h-4 w-4 text-blue-400" />
                  <span className="text-xl font-semibold text-white">{tenant.alerts_24h}</span>
                </div>
                <p className="text-xs text-gray-400">Alerts (24h)</p>
              </div>

              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Users className="h-4 w-4 text-green-400" />
                  <span className="text-xl font-semibold text-white">{tenant.active_users}</span>
                </div>
                <p className="text-xs text-gray-400">Active Users</p>
              </div>

              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Server className="h-4 w-4 text-purple-400" />
                  <span className="text-xl font-semibold text-white">
                    {tenant.connectors_healthy}/{tenant.connectors_total}
                  </span>
                </div>
                <p className="text-xs text-gray-400">Connectors</p>
              </div>
            </div>

            {/* Last Activity */}
            {tenant.last_activity && (
              <div className="flex items-center gap-2 text-xs text-gray-400 mb-3 pb-3 border-b border-gray-700">
                <Clock className="h-3.5 w-3.5" />
                Last activity: {new Date(tenant.last_activity).toLocaleString()}
              </div>
            )}

            {/* Issues */}
            {tenant.issues.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-gray-400 mb-2">Issues Detected:</p>
                {tenant.issues.map((issue, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-xs">
                    <AlertCircle className="h-3.5 w-3.5 text-yellow-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">{issue}</span>
                  </div>
                ))}
              </div>
            )}

            {tenant.issues.length === 0 && (
              <div className="flex items-center gap-2 text-xs text-green-400">
                <CheckCircle2 className="h-3.5 w-3.5" />
                <span>No issues detected</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredTenants.length === 0 && (
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-12 text-center">
          <Activity className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No Tenants Found</h3>
          <p className="text-gray-400">No tenants match the selected filter criteria</p>
        </div>
      )}
    </div>
  );
};

export default TenantHealth;
